import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { FieldType } from 'types/FieldTypes';
import { generateFieldComponents } from 'components/forms/GenerateFieldComponents';
import { BillingData, PaymentMethod } from '../BillingTypes';
import { CardElement, IbanElement, useElements } from '@stripe/react-stripe-js';
import { StripeCardElement, StripeIbanElement } from '@stripe/stripe-js';
import Dropdown from 'components/forms/basefields/Dropdown';
import FormFieldContext from 'components/forms/FormFieldContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';

interface BillingDetailsProps {
    billingData: BillingData;
    paymentMethodData: PaymentMethod;
    showValidationErrors: boolean;
    onPaymentMethodEntered: (isPaymentMethodEntered: boolean) => void;
    onUpdateData?: (updatedData: Record<string, any>) => void;
}

export interface BillingDetailsRef {
    getCardElement: () => StripeCardElement | null;
    getIbanElement: () => StripeIbanElement | null;
}

const BillingDetails = forwardRef<BillingDetailsRef, BillingDetailsProps>(({ 
    billingData, paymentMethodData, showValidationErrors, onPaymentMethodEntered, onUpdateData 
}, ref) => {
    const { t } = useTranslation();
    const { activeFeatures } = useAuthContext();
    const viewKey = useMemo(() => uuidv4(), []);
    const [editing, setEditing] = useState(true);
    const [updatedData, setUpdatedData] = useState<Record<string, any>>({});
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>(paymentMethodData['payment_type'] || 'card');
    const [isPaymentMethodEntered, setIsPaymentMethodEntered] = useState(false);
    const elements = useElements();

    const [paymentMethods] = useState<{ value: string, name: string }[]>([ 
        // { value: 'card', name: 'billing.payment_method.creditcard' }, 
        { value: 'sepa_debit', name: 'billing.payment_method.sepa' }, 
        // { value: 'ideal', name: 'billing.payment_method.ideal' },  
    ]);

    useImperativeHandle(ref, () => ({
        getCardElement: () => elements?.getElement(CardElement) as StripeCardElement | null,
        getIbanElement: () => elements?.getElement(IbanElement) as StripeIbanElement | null,
    }));

    const contactFields: FieldType[] = [
        {
            type: 'text',
            name: 'company_name',
            label: 'billing.general.company_name_label',
            placeholder: 'billing.general.company_name_label',
            invalid: showValidationErrors && !billingData.company_name
        },
        {
            type: 'text',
            name: 'first_name',
            label: 'billing.general.first_name_label',
            placeholder: 'billing.general.first_name_label',
            width: '50%',
            group: 'field-row',
            invalid: showValidationErrors && !billingData.first_name
        },
        {
            type: 'text',
            name: 'last_name',
            label: 'billing.general.last_name_label',
            placeholder: 'billing.general.last_name_label',
            width: '50%',
            group: 'field-row',
            invalid: showValidationErrors && !billingData.last_name
        },
        {
            type: 'email',
            name: 'email',
            label: 'billing.general.correspondence_email_label',
            placeholder: 'billing.general.email_placeholder',
            helperText: 'billing.general.correspondence_email_helperText',
            invalid: showValidationErrors && !billingData.email
        },
        {
            type: 'email',
            name: 'invoice_email',
            label: 'billing.general.invoice_email_label',
            placeholder: 'billing.general.email_placeholder',
            helperText: 'billing.general.invoice_email_helperText'
        }
    ];

    const invoiceAddressFields: FieldType[] = [
        {
            type: 'text',
            name: 'street',
            label: 'billing.general.street_label',
            placeholder: 'billing.general.street_label',
            width: '70%',
            group: 'field-row',
            invalid: showValidationErrors && !billingData.street
        },
        {
            type: 'text',
            name: 'house_number',
            label: 'billing.general.house_number_label',
            placeholder: 'billing.general.house_number_label',
            width: '30%',
            group: 'field-row',
            invalid: showValidationErrors && !billingData.house_number
        },
        {
            type: 'text',
            name: 'postal_code',
            label: 'billing.general.postal_code_label',
            placeholder: 'billing.general.postal_code_label',
            width: '40%',
            group: 'field-row-next',
        },
        {
            type: 'text',
            name: 'city',
            label: 'billing.general.city_label',
            placeholder: 'billing.general.city_label',
            width: '60%',
            group: 'field-row-next',
            invalid: showValidationErrors && !billingData.city
        },
        {
            type: 'text',
            name: 'country',
            label: 'billing.general.country_label',
            placeholder: 'billing.general.country_label',
        },
        {
            type: 'text',
            name: 'vat_number',
            label: 'billing.general.vat_number_label',
            placeholder: 'billing.general.vat_number_placeholder',
        },
    ];

    // Optionally return the changed data to the parent component
    useEffect(() => {
        if (onUpdateData) {
            onUpdateData(updatedData)
        }
    }, [updatedData]);

    // Return the is payment method entered state to the parent component
    useEffect(() => {
        onPaymentMethodEntered(isPaymentMethodEntered);
    }, [isPaymentMethodEntered]);

    return (
        <div className='wizard-step'>
            <div className='header-title'>
                <h2>{t('billing.general.billing_data_header')}</h2>
            </div>
            <div className='step-content'>
                <FormFieldContext.Provider value={{ editing, setEditing, updatedData, setUpdatedData }}>
                    <form className='formset billing-details'>
                        <div className='card'>
                            <h4>{t('billing.general.company_details_header')}</h4>
                            <div className='card-details'>
                                <div className='company-fields'>
                                    {generateFieldComponents(viewKey, contactFields, undefined, undefined, billingData, updatedData, activeFeatures)}
                                </div>     
                            </div>     
                        </div>

                        <div className='card'>
                            <h4>{t('billing.general.invoice_address_header')}</h4>
                            <div className='card-details'>
                                <div className='address-fields'>
                                    {generateFieldComponents(viewKey, invoiceAddressFields, undefined, undefined, billingData, updatedData, activeFeatures)}
                                </div>        
                            </div>                            
                        </div>

                        <div className='card'>
                            <h4>{t('billing.general.payment_method_header')}</h4>
                            <div className='card-details'>
                                <div className='billing-fields'>
                                    <div className='formfield'>
                                        <label>
                                            {t('billing.general.payment_method_label')}
                                            {showValidationErrors && !isPaymentMethodEntered && (
                                                <span className='mandatory-error'>
                                                    {t('billing.general.mandatory_field')}
                                                </span>
                                            )}
                                        </label>
                                        <Dropdown<{ value: string, name: string }>
                                            options={paymentMethods}
                                            name='payment_method'
                                            disabled_selected={t('billing.general.select_payment_method_disabled_selected')}
                                            selectedOption={paymentMethods.find(method => method.value === selectedPaymentMethod)}
                                            onChange={(selectedValue) => {
                                                setSelectedPaymentMethod(selectedValue);
                                                setUpdatedData({...updatedData, ['payment_type']: selectedValue})
                                            }}
                                            selectionFormat={(option) => `${option.name}`}
                                            optionFormat='name'
                                            allowNoneOption={false} 
                                            showSearch={false}
                                        />
                                    </div>

                                    {/* {selectedPaymentMethod === 'card' && (
                                        <div>
                                            <div className='formfield'>
                                                <label>
                                                    {t('billing.general.card_name_label')}
                                                </label>
                                                <input
                                                    type="text"
                                                    value={paymentMethodData['card_owner_name'] ?? ''}
                                                    onChange={(e) => setUpdatedData({...updatedData, ['card_owner_name']: e.target.value})}
                                                    placeholder={t('billing.general.card_name_label')}
                                                    className='text'
                                                />
                                            </div>
                                            <div className='formfield'>
                                                <label>
                                                    {t('billing.general.card_number_label')}
                                                </label>
                                                <div className='credit-card-field'>
                                                    <CardElement
                                                        options={{
                                                            hidePostalCode: true,
                                                            style: {
                                                                base: {
                                                                    fontSize: '1rem',
                                                                    fontFamily: 'Gotham-Book',
                                                                    color: '#1a1d27',
                                                                    '::placeholder': {
                                                                        color: '#757575',
                                                                    },
                                                                }
                                                            }
                                                        }}
                                                        onChange={(e) => setIsPaymentMethodEntered(e.complete)}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )} */}

                                    {/* {selectedPaymentMethod === 'ideal' && (
                                        <div className='formfield'>
                                            <label>{t('billing.general.bank_label')}</label>
                                            <div className='ideal-field'>
                                                <IdealBankElement
                                                    options={{
                                                        style: {
                                                            base: {
                                                                fontSize: '1rem',
                                                                fontFamily: 'Gotham-Book',
                                                                color: '#1a1d27',
                                                                backgroundColor: 'white',                     
                                                                '::placeholder': {
                                                                    color: '#757575',
                                                                },
                                                            }
                                                        },
                                                    }}
                                                    
                                                />
                                            </div>
                                        </div>
                                    )} */}

                                    {selectedPaymentMethod === 'sepa_debit' && (
                                        <div>
                                            <div className='formfield'>
                                                <label>
                                                    {t('billing.general.iban_holder_name_label')}
                                                </label>
                                                <input
                                                    type="text"
                                                    value={paymentMethodData['sepa_account_holder'] ?? ''}
                                                    onChange={(e) => setUpdatedData({...updatedData, ['sepa_account_holder']: e.target.value})}
                                                    placeholder={t('billing.general.iban_holder_name_label')}
                                                    className='text'
                                                />
                                            </div>
                                            <div className='formfield'>
                                                <label>
                                                    {t('billing.general.iban_number_label')}
                                                </label>
                                                <div className='sepa-field'>
                                                    <IbanElement
                                                        options={{
                                                            supportedCountries: ['SEPA'],
                                                            style: {
                                                                base: {
                                                                    fontSize: '1rem',
                                                                    fontFamily: 'Gotham-Book',
                                                                    color: '#1a1d27',
                                                                    '::placeholder': {
                                                                        color: '#757575',
                                                                    },
                                                                },
                                                            },
                                                        }}
                                                        onChange={(e) => setIsPaymentMethodEntered(e.complete)}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>    
                            </div>        
                        </div>
                    </form>
                </FormFieldContext.Provider>
            </div>
        </div>
    )
})

export default BillingDetails;